<template>
    <div v-if="unavailable" class="audio-unavailable">
        <v-icon size="14" color="txtMuted">mdi-volume-off</v-icon>
        <span>{{ unavailableText }}</span>
    </div>
    <div v-else class="audio-player">
        <button type="button" class="ap-btn" @click="toggle">
            <v-icon size="15" color="white">{{ playing ? 'mdi-pause' : 'mdi-play' }}</v-icon>
        </button>
        <div class="ap-track" ref="track" @click="seek">
            <div class="ap-fill" :style="{ width: `${progress}%` }">
                <span class="ap-knob" />
            </div>
        </div>
        <span class="ap-time">{{ displayTime }}</span>
        <audio ref="audio" :src="src" preload="metadata" @loadedmetadata="onMeta" @timeupdate="onTime"
            @play="playing = true" @pause="playing = false" @ended="onEnded" @error="onError" />
    </div>
</template>

<script>
import { canPlayAudioUrl } from '@/utils/Recorder'

export default {
    name: "AudioPlayer",
    props: {
        src: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            playing: false,
            current: 0,
            duration: 0,
            failed: false,
        }
    },
    computed: {
        supported(vm = this) {
            return canPlayAudioUrl(vm.src)
        },
        unavailable(vm = this) {
            return !vm.supported || vm.failed
        },
        unavailableText(vm = this) {
            return vm.supported ? vm.$t('common.audio_load_error') : vm.$t('common.audio_not_supported')
        },
        hasDuration(vm = this) {
            return isFinite(vm.duration) && vm.duration > 0
        },
        progress(vm = this) {
            if (!vm.hasDuration) return 0
            return Math.min(100, (vm.current / vm.duration) * 100)
        },
        displayTime(vm = this) {
            if (vm.hasDuration) return `${vm.format(vm.current)} / ${vm.format(vm.duration)}`
            return vm.format(vm.current)
        },
    },
    methods: {
        format(seconds) {
            if (!isFinite(seconds)) return '0:00'
            const m = Math.floor(seconds / 60)
            const s = Math.floor(seconds % 60)
            return `${m}:${s < 10 ? '0' : ''}${s}`
        },
        toggle() {
            const vm = this
            const audio = vm.$refs.audio
            if (!audio) return
            if (audio.paused) {
                const promise = audio.play()
                if (promise && promise.catch) promise.catch(() => {})
            } else {
                audio.pause()
            }
        },
        seek(event) {
            const vm = this
            const audio = vm.$refs.audio
            const track = vm.$refs.track
            if (!audio || !track || !vm.hasDuration) return
            const rect = track.getBoundingClientRect()
            const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
            audio.currentTime = ratio * vm.duration
            vm.current = audio.currentTime
        },
        onMeta() {
            const vm = this
            vm.duration = vm.$refs.audio?.duration || 0
        },
        onTime() {
            const vm = this
            vm.current = vm.$refs.audio?.currentTime || 0
        },
        onEnded() {
            const vm = this
            vm.playing = false
            vm.current = 0
        },
        onError() {
            const vm = this
            vm.playing = false
            vm.failed = true
        },
    },
    watch: {
        src() {
            this.failed = false
        },
    },
    beforeDestroy() {
        const audio = this.$refs.audio
        if (audio) { try { audio.pause() } catch (e) {} }
    },
}
</script>

<style scoped>
.audio-unavailable {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    font-size: 12px;
    line-height: 1.35;
    color: var(--v-txtMuted-base);
}

.audio-player {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
}

.ap-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: none;
    background: var(--v-accent-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.ap-track {
    flex: 1;
    min-width: 0;
    height: 5px;
    border-radius: 3px;
    background: var(--v-outline-base);
    cursor: pointer;
    position: relative;
}

.ap-fill {
    height: 100%;
    border-radius: 3px;
    background: var(--v-accent-base);
    position: relative;
}

.ap-knob {
    position: absolute;
    right: -5px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--v-accent-base);
}

.ap-time {
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    white-space: nowrap;
}
</style>
